import { Card, Title, Text, Group, Badge, Stack, Button } from "@mantine/core";
import { IconEdit, IconDownload } from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AWARD_SHEET_FIELD_LABELS,
  currencyToDhivehiWords,
  formatAwardSheetMeetingDateTime,
  isChairpersonVoteIncluded,
  numberToDhivehi,
} from "@hmh-proc/shared";
import {
  DhivehiText,
  FormFieldsTable,
  FormTitle,
  LtrText,
  MemberVotesTable,
  MyApprovalsHint,
  OfficerBox,
  membersListedOnForm,
  type FormFieldRow,
} from "../components/DhivehiFormSections";
import { useAwardSheet, useSendAwardSheetForApproval, useGenerateAwardSheetPdf } from "../hooks";
import { useAuth } from "../../../auth/AuthContext";
import { approvalDisplayName, singleSourceRtlLayoutStyle } from "../../../utils/dhivehiText";

const L = AWARD_SHEET_FIELD_LABELS;
const officerLabels = { name: L.nameLabel, signature: L.signatureLabel, rejected: L.rejected };

const statusBadge = (status: string) => {
  switch (status) {
    case "DRAFT":
      return { color: "gray", label: "Draft" };
    case "APPROVED":
      return { color: "green", label: "Approved" };
    case "REJECTED":
      return { color: "red", label: "Rejected" };
    default:
      return { color: "yellow", label: "Pending approval" };
  }
};

/** "100,249.99ރ (އެއްލައްކަ ... ރުފިޔާ ...)", as on the printed sheet. */
function priceWithWords(amount: number | string | null | undefined): string | null {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  const num = Number(amount);
  const figure = num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  try {
    return `${figure}ރ (${currencyToDhivehiWords(num)})`;
  } catch {
    return `${figure}ރ`;
  }
}

/** "15 (ފަނަރަ) ދުވަސް", as on the printed sheet. */
function durationWithWords(days: number | null | undefined): string | null {
  if (days == null || Number.isNaN(Number(days))) return null;
  try {
    return `${days} (${numberToDhivehi(Number(days))}) ${L.daysSuffix}`;
  } catch {
    return `${days} ${L.daysSuffix}`;
  }
}

export const AwardSheetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sendForApproval = useSendAwardSheetForApproval();
  const generatePdf = useGenerateAwardSheetPdf();
  const { data: awardSheet, isLoading, error } = useAwardSheet(id || "");

  if (isLoading) {
    return (
      <Stack gap="lg" align="center">
        <Text size="xl">Loading award sheet details...</Text>
      </Stack>
    );
  }

  if (error || !awardSheet) {
    return (
      <Stack gap="lg" align="center">
        <Text size="xl" c="red">
          Failed to load award sheet details
        </Text>
      </Stack>
    );
  }

  const meeting = awardSheet.meeting;
  const attendances = meeting?.attendances ?? [];
  const chairAttendance = attendances.find((a) => a.isChairperson);
  const secretaryAttendance = attendances.find((a) => a.isSecretary);
  const approvals = awardSheet.approvals ?? [];
  const myApproval = approvals.find((a) => a.memberId === user?.id);
  const headerStatus = statusBadge(awardSheet.status);

  const members = membersListedOnForm(approvals, {
    chairId: chairAttendance?.memberId,
    secretaryId: secretaryAttendance?.memberId,
    chairVotes: isChairpersonVoteIncluded(attendances),
  });

  // As on the printed sheet, an officer's signature shows once they approve (or the whole sheet is approved).
  const officer = (memberId?: string) => {
    const approval = approvals.find((a) => a.memberId === memberId);
    return {
      signed: approval?.status === "APPROVED" || awardSheet.status === "APPROVED",
      signedAt: approval?.approvedAt,
      rejected: approval?.status === "REJECTED",
    };
  };

  const meetingDate = formatAwardSheetMeetingDateTime(meeting?.plannedStart, meeting?.actualEnd);
  const year = awardSheet.year ?? (meeting?.plannedStart ? new Date(meeting.plannedStart).getFullYear() : "");

  const rows: FormFieldRow[] = [
    { label: L.date, value: <DhivehiText value={meetingDate === "—" ? null : meetingDate} /> },
    { label: L.meetingNumber, value: <LtrText value={meeting?.meetingNumber} /> },
    { label: L.tenderName, value: <DhivehiText value={awardSheet.tenderName} /> },
    { label: L.location, value: <DhivehiText value={awardSheet.location} /> },
    {
      label: L.tenderOrQuotationNumber,
      value: <LtrText value={awardSheet.quotationNumber || awardSheet.tenderNumber} />,
    },
    { label: L.awardedVendor, value: <DhivehiText value={awardSheet.vendor} /> },
    { label: L.tenderPrice, value: <DhivehiText value={priceWithWords(awardSheet.proposedCost)} /> },
    { label: L.duration, value: <DhivehiText value={durationWithWords(awardSheet.proposedDuration)} /> },
    {
      label: L.decision,
      block: true,
      value: (
        <Stack gap="xs">
          <DhivehiText value={awardSheet.decisionText} multiline />
          {awardSheet.justification?.trim() && <DhivehiText value={awardSheet.justification} multiline />}
        </Stack>
      ),
    },
    { label: L.detail, block: true, value: <DhivehiText value={awardSheet.detail} multiline /> },
  ];

  return (
    <Stack gap="lg">
      <Card>
        <Group justify="space-between">
          <div>
            <Title order={2}>{awardSheet.awardSheetNumber || awardSheet.tenderNumber}</Title>
            <Text c="dimmed" size="sm">
              {meeting?.title ?? "Award Sheet Details"}
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() =>
                generatePdf.mutate({
                  id: awardSheet.id,
                  fileName: `AS-${(awardSheet.awardSheetNumber || awardSheet.id).replace(/[^\w.-]+/g, "_")}.pdf`,
                })
              }
              loading={generatePdf.isPending}
            >
              Download PDF
            </Button>
            {awardSheet.status === "DRAFT" && (
              <>
                <Button
                  variant="outline"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => navigate(`/bid-committee/award-sheets/${awardSheet.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  onClick={() => sendForApproval.mutate(awardSheet.id)}
                  loading={sendForApproval.isPending}
                >
                  Send For Approval
                </Button>
              </>
            )}
            <Badge color={headerStatus.color} size="lg">
              {headerStatus.label}
            </Badge>
          </Group>
        </Group>
      </Card>

      {awardSheet.status === "PENDING" && myApproval?.status === "PENDING" && (
        <MyApprovalsHint what="award sheet" />
      )}

      <Card style={singleSourceRtlLayoutStyle()}>
        <Stack gap="lg">
          <FormTitle
            title={L.pdfDocumentTitle}
            subtitle={`${L.committeeYearTitle} ${year}`}
            number={awardSheet.awardSheetNumber}
          />

          <FormFieldsTable rows={rows} />

          <MemberVotesTable
            heading={L.acknowledgement}
            text={awardSheet.acknowledgement?.trim() ? awardSheet.acknowledgement : L.acknowledgementDefault}
            columns={{ name: L.colMemberName, office: L.colOffice, approve: L.colApproved, reject: L.colRejected }}
            marks={{ approve: L.approved, reject: L.rejected }}
            office={L.defaultOffice}
            votes={members}
          />

          <OfficerBox
            title={L.chairPerson}
            labels={officerLabels}
            name={approvalDisplayName(chairAttendance?.member)}
            {...officer(chairAttendance?.memberId)}
          />

          <OfficerBox
            title={L.secretary}
            labels={officerLabels}
            name={approvalDisplayName(secretaryAttendance?.member)}
            {...officer(secretaryAttendance?.memberId)}
          />
        </Stack>
      </Card>
    </Stack>
  );
};
